// src/services/socialAuthService.ts

import { SocialProvider, SocialAuthCredentials } from '../types/auth';

interface OAuthConfig {
  clientId: string;
  redirectUri: string;
  scope: string;
  authUrl: string;
}

const configs: Record<SocialProvider, OAuthConfig> = {
  github: {
    clientId: process.env.REACT_APP_GITHUB_CLIENT_ID || '',
    redirectUri: `${window.location.origin}/auth/github/callback`,
    scope: 'user:email',
    authUrl: 'https://github.com/login/oauth/authorize'
  },
  linkedin: {
    clientId: process.env.REACT_APP_LINKEDIN_CLIENT_ID || '',
    redirectUri: `${window.location.origin}/auth/linkedin/callback`,
    scope: 'profile email',
    authUrl: 'https://www.linkedin.com/oauth/v2/authorization'
  },
  google: {
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
    redirectUri: `${window.location.origin}/auth/google/callback`,
    scope: 'profile email',
    authUrl: 'https://accounts.google.com/o/oauth2/v2/auth'
  }
};

class SocialAuthService {
  private static instance: SocialAuthService;
  private usedStates = new Set<string>();
  private stateCleanupInterval: NodeJS.Timeout | null = null;

  private constructor() {
    // Clean up used states every 10 minutes to prevent memory leaks
    this.stateCleanupInterval = setInterval(() => {
      this.cleanupUsedStates();
    }, 10 * 60 * 1000);
  }

  static getInstance(): SocialAuthService {
    if (!SocialAuthService.instance) {
      SocialAuthService.instance = new SocialAuthService();
    }
    return SocialAuthService.instance;
  }

  async initiateOAuth(provider: SocialProvider): Promise<void> {
    const config = configs[provider];
    const state = this.generateState();
    
    // Clear any existing OAuth state to prevent conflicts
    this.clearOAuthState();
    
    localStorage.setItem('oauth_state', state);
    localStorage.setItem('oauth_provider', provider);
    localStorage.setItem('oauth_timestamp', Date.now().toString());

    const params = new URLSearchParams({
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      scope: config.scope,
      response_type: 'code',
      state
    });

    window.location.href = `${config.authUrl}?${params.toString()}`;
  }

  async validateCallback(credentials: SocialAuthCredentials): Promise<SocialAuthCredentials> {
    const savedState = localStorage.getItem('oauth_state');
    const savedProvider = localStorage.getItem('oauth_provider');
    const timestamp = localStorage.getItem('oauth_timestamp');
    
    // Check if state exists and matches
    if (!credentials.state || !savedState || credentials.state !== savedState) {
      this.clearOAuthState();
      throw new Error('Invalid OAuth state');
    }

    // Check if provider matches
    if (!savedProvider || credentials.provider !== savedProvider) {
      this.clearOAuthState();
      throw new Error('OAuth provider mismatch');
    }

    // Check if the state has been used before
    if (this.usedStates.has(credentials.state)) {
      this.clearOAuthState();
      throw new Error('OAuth state already used');
    }

    // Check if the OAuth flow is not too old (5 minutes timeout)
    if (timestamp) {
      const now = Date.now();
      const oauthTime = parseInt(timestamp, 10);
      if (now - oauthTime > 5 * 60 * 1000) { // 5 minutes
        this.clearOAuthState();
        throw new Error('OAuth flow expired');
      }
    }

    // Mark this state as used
    this.usedStates.add(credentials.state);
    
    // Clean up localStorage
    this.clearOAuthState();
    
    return credentials;
  }

  private clearOAuthState(): void {
    localStorage.removeItem('oauth_state');
    localStorage.removeItem('oauth_provider');
    localStorage.removeItem('oauth_timestamp');
  }

  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  private cleanupUsedStates(): void {
    // Keep only the last 100 used states to prevent memory leaks
    if (this.usedStates.size > 100) {
      const statesToKeep = Array.from(this.usedStates).slice(-50);
      this.usedStates.clear();
      statesToKeep.forEach(state => this.usedStates.add(state));
    }
  }
}

export const socialAuthService = SocialAuthService.getInstance();
